import Tabs from "components/EvtKit/tabs/Tabs"
import TabsContent from "components/EvtKit/tabs/TabsContent"
import { memo, useCallback, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import styled from "styled-components"
import ProductsBenefits from "./ProductsBenefits"
import PageLayout from "components/PageLayout"
import CreateProductTabList from "./tabs/CreateProductTabList"
import { productFormHeadingText, submitionError, submitionSuccess, tabIds } from "../../constants/defaultValues"
import { useAppDispatch } from "reducers/store"
import { showToast } from "reducers/toast/toast-actions"
import useProduct from "../../hooks/useProduct"
import ProductForm from "../../modals/ProductForm"
import IsExternal from "../../modals/IsExternal"
import IsFederated from "../../modals/IsFederated"
import IntegrationForm from "../../modals/IntegrationForm"
import useProductRequest from "../../hooks/useProductRequest"
import { mapProductToSubmition } from "../../mappers/mapProductForm"
import { useProductIdentifiers } from "../../hooks/useProductIdentifiers"
import NavigateButtons from "./NavigateButtons"
import ProductBannerModal from "./modal/ProductBannerModal/ProductBannerModal"
import Heading from "components/Typography/Heading"

const ContentContainer = styled.div`
  display: flex;
  width: 98%;
  gap: 8px;
  margin: 24px 0 20px 0;
`

const CardContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: ${(props) => props.theme.palette.white};
  padding: 16px 16px 0px 16px;
  box-shadow: 0px 2px 6px 0px ${(props) => props.theme.palette.box_shadow};
`

const SeparatorLine = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${(props) => props.theme.palette.mediumDarkGray};
  opacity: 0.3;
  margin-top: 12px;
  margin-bottom: 28px;
`

const ProductCreation = () => {
  const { isValidProduct, productToSubmit, formData, benefits } = useProduct()

  const { productForm, bannerInfo, isFederatedForm, isExternalForm, integrationForm, productInfo } = formData

  const appDispatch = useAppDispatch()

  const navigate = useNavigate()

  const { identifiersCleanup } = useProductIdentifiers()

  const onSuccess = useCallback(() => {
    appDispatch(showToast(submitionSuccess.create, "success"))
  }, [])

  const navigateToProductPage = useCallback(() => {
    onSuccess()
    navigate("/products")
  }, [])

  const { onSubmit, isLoading } = useProductRequest("create", navigateToProductPage, submitionError.create)

  const handleSubmit = useCallback(() => {
    const productData = mapProductToSubmition(productToSubmit.data, benefits.benefitsList)

    onSubmit(productData)
  }, [onSubmit, productToSubmit.data, benefits.benefitsList])

  useEffect(() => {
    return () => {
      identifiersCleanup()
    }
  }, [])

  return (
    <PageLayout title="Produtos">
      <ContentContainer>
        <Tabs defaultActiveTabId={tabIds.product}>
          <CardContainer>
            <CreateProductTabList isBenefitStepDisabled={!isValidProduct} />
            <TabsContent triggerId={tabIds.product}>
              <ProductForm headingText={productFormHeadingText.create} {...productForm} {...productInfo} />
              <SeparatorLine />
              <Heading variant="h6">
                Ative as opções que desejar, fornecendo as informações necessárias para cada campo:
              </Heading>
              <IsFederated {...isFederatedForm} {...productInfo} />
              <IsExternal {...isExternalForm} />
              <IntegrationForm {...integrationForm} modalType="create" />
              <NavigateButtons isValidProductForm={isValidProduct} />
            </TabsContent>
            <TabsContent triggerId={tabIds.benefits}>
              <ProductsBenefits {...benefits} isLoading={isLoading} onSubmit={handleSubmit} />
            </TabsContent>
          </CardContainer>
        </Tabs>
      </ContentContainer>
      <ProductBannerModal {...bannerInfo} />
    </PageLayout>
  )
}

export default memo(ProductCreation)
